/* ==========================================================================
   DIDO · Seasonal layer: snow (winter) or falling leaves (autumn).
   One canvas, pre-rendered sprites, three depth layers, wind gusts and
   scroll parallax. Visitors can pause or play it with the floating button;
   their choice is remembered. Off by default for reduced motion and data
   saver, paused while the tab is hidden.
   ========================================================================== */
(function () {
  'use strict';
  var cfg = window.DIDO_SEASON || {};
  var season = cfg.season === 'autumn' ? 'autumn' : 'winter';
  var KEY = 'dido-season-fx';
  var btn = document.querySelector('[data-fx-toggle]');

  var small = window.matchMedia('(max-width: 749px)').matches;
  if (!cfg.animate || (small && !cfg.mobile)) { if (btn) btn.remove(); return; }

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  function readPref() { try { return window.localStorage.getItem(KEY); } catch (e) { return null; } }
  function writePref(v) { try { window.localStorage.setItem(KEY, v); } catch (e) {} }
  var pref = readPref();
  var wantOn = pref ? pref === 'on' : !(reduce.matches || saveData);

  var finePointer = window.matchMedia('(pointer: fine)').matches;
  var dpr = Math.min(window.devicePixelRatio || 1, 2);
  var W = window.innerWidth, H = window.innerHeight;
  var canvas = null, ctx = null, sprites = null, parts = [];
  var raf = 0, last = 0, running = false;
  var master = 0, masterTarget = 0;          /* global fade for play/pause */
  var wind = 0, windTarget = 0.25, windTimer = 0;
  var lastScroll = window.scrollY, scrollDelta = 0;
  var px = -9999, py = -9999;

  function rand(a, b) { return a + Math.random() * (b - a); }

  /* ---------- Colour helpers (autumn leaves) ---------- */
  function hexToRgb(h) { var n = parseInt(h.slice(1), 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; }
  function mix(a, b, t) { var x = hexToRgb(a), y = hexToRgb(b); return 'rgb(' + [0, 1, 2].map(function (i) { return Math.round(x[i] + (y[i] - x[i]) * t); }).join(',') + ')'; }
  var CREAM = '#fbf7f4', INK = '#2a1622';

  /* ---------- Sprite factory ---------- */
  function sprite(size, draw, blur) {
    var c = document.createElement('canvas');
    var pad = Math.ceil(size * 0.25) + 6;
    var full = size + pad * 2;
    c.width = c.height = Math.ceil(full * dpr);
    var g = c.getContext('2d');
    g.scale(dpr, dpr);
    g.translate(full / 2, full / 2);
    if (blur && 'filter' in g) g.filter = 'blur(' + blur + 'px)';
    draw(g, size);
    return { c: c, s: full, b: size };
  }

  /* Winter: soft dot, two procedural crystals */
  function drawDot(g, size) {
    var r = size / 2;
    g.shadowColor = 'rgba(42, 22, 34, 0.35)';
    g.shadowBlur = 3;
    /* white core, faintly cool edge so it still reads on cream */
    var grad = g.createRadialGradient(0, 0, 0, 0, 0, r);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.6, 'rgba(244,240,245,1)');
    grad.addColorStop(0.85, 'rgba(226,219,228,0.9)');
    grad.addColorStop(1, 'rgba(226,219,228,0)');
    g.fillStyle = grad;
    g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.fill();
  }
  function crystal(kind) {
    return function (g, size) {
      var R = size / 2;
      g.strokeStyle = '#ffffff';
      g.lineCap = 'round';
      g.lineWidth = Math.max(1.2, size / 18);
      g.shadowColor = 'rgba(42, 22, 34, 0.45)';
      g.shadowBlur = 4;
      for (var i = 0; i < 6; i++) {
        g.save();
        g.rotate(i * Math.PI / 3);
        g.beginPath(); g.moveTo(0, 0); g.lineTo(0, -R); g.stroke();
        if (kind === 0) {
          /* fern-like dendrite: two pairs of branches */
          [[0.42, 0.28], [0.7, 0.2]].forEach(function (b) {
            g.beginPath();
            g.moveTo(0, -R * b[0]); g.lineTo(-R * b[1], -R * (b[0] + b[1]));
            g.moveTo(0, -R * b[0]); g.lineTo(R * b[1], -R * (b[0] + b[1]));
            g.stroke();
          });
        } else {
          /* plate star: V tips and a side spur */
          g.beginPath();
          g.moveTo(-R * 0.2, -R * 0.8); g.lineTo(0, -R * 0.62); g.lineTo(R * 0.2, -R * 0.8);
          g.stroke();
          g.beginPath();
          g.moveTo(0, -R * 0.34); g.lineTo(R * 0.34 * Math.sin(Math.PI / 3), -R * 0.34 * Math.cos(Math.PI / 3));
          g.stroke();
        }
        g.restore();
      }
      g.fillStyle = '#ffffff';
      g.beginPath(); g.arc(0, 0, Math.max(1, size / 16), 0, Math.PI * 2); g.fill();
    };
  }

  /* Autumn: four leaf outlines in a 100 unit box, stem at the bottom */
  var LEAVES = [
    { path: 'M50 6L58 26L70 18L66 38L88 32L80 48L94 54L74 62L78 74L58 68L54 84L50 80L46 84L42 68L22 74L26 62L6 54L20 48L12 32L34 38L30 18L42 26Z',
      veins: [[50, 80, 50, 12], [50, 62, 84, 36], [50, 62, 16, 36], [50, 66, 86, 56], [50, 66, 14, 56]] },
    { path: 'M50 8Q60 10 58 20Q70 18 66 30Q78 32 70 44Q82 48 70 58Q78 68 64 70Q66 82 54 80L50 88L46 80Q34 82 36 70Q22 68 30 58Q18 48 30 44Q22 32 34 30Q30 18 42 20Q40 10 50 8Z',
      veins: [[50, 86, 50, 14], [50, 34, 64, 26], [50, 34, 36, 26], [50, 50, 68, 46], [50, 50, 32, 46], [50, 64, 62, 66], [50, 64, 38, 66]] },
    { path: 'M50 6C72 20 80 48 66 72C60 82 54 86 50 90C46 86 40 82 34 72C20 48 28 20 50 6Z',
      veins: [[50, 88, 50, 12], [50, 36, 62, 28], [50, 36, 38, 28], [50, 52, 66, 44], [50, 52, 34, 44], [50, 68, 62, 64], [50, 68, 38, 64]] },
    { path: 'M50 14C76 14 86 40 74 62C66 76 56 82 50 88C44 82 34 76 26 62C14 40 24 14 50 14Z',
      veins: [[50, 86, 50, 20], [50, 44, 70, 34], [50, 44, 30, 34], [50, 62, 70, 56], [50, 62, 30, 56]] }
  ];
  var LEAF_COLOURS = [
    { c: '#9c4a2a', w: 30 },  /* rust */
    { c: '#c2873a', w: 24 },  /* amber */
    { c: '#a8742c', w: 20 },  /* ochre */
    { c: '#7a2e22', w: 16 },  /* russet brown */
    { c: '#6e1f56', w: 10 }   /* DIDO wine */
  ];
  function weightedColour() {
    var total = 0, i;
    for (i = 0; i < LEAF_COLOURS.length; i++) total += LEAF_COLOURS[i].w;
    var r = Math.random() * total;
    for (i = 0; i < LEAF_COLOURS.length; i++) { r -= LEAF_COLOURS[i].w; if (r <= 0) return i; }
    return 0;
  }
  function leafDraw(shape, colour, back) {
    var p = new Path2D(shape.path);
    return function (g, size) {
      var k = size / 100;
      g.scale(k, k);
      g.translate(-50, -50);
      g.shadowColor = 'rgba(42, 22, 34, 0.25)';
      g.shadowBlur = 4 / k;
      g.fillStyle = back ? mix(colour, CREAM, 0.32) : colour;
      g.fill(p);
      g.shadowColor = 'transparent';
      g.strokeStyle = back ? mix(colour, CREAM, 0.55) : mix(colour, INK, 0.38);
      g.lineWidth = 1.6;
      g.lineCap = 'round';
      g.beginPath();
      shape.veins.forEach(function (v) { g.moveTo(v[0], v[1]); g.lineTo(v[2], v[3]); });
      g.stroke();
      /* stem */
      g.lineWidth = 2.4;
      g.strokeStyle = mix(colour, INK, 0.45);
      g.beginPath(); g.moveTo(50, 84); g.lineTo(50, 98); g.stroke();
    };
  }

  function buildSprites() {
    if (season === 'winter') {
      return {
        dot: sprite(12, drawDot),
        dotSoft: sprite(18, drawDot, 1.5),
        crystals: [sprite(40, crystal(0)), sprite(40, crystal(1))],
        crystalsSoft: [sprite(56, crystal(0), 1.8), sprite(56, crystal(1), 1.8)]
      };
    }
    var leaves = [];
    LEAVES.forEach(function (shape) {
      LEAF_COLOURS.forEach(function (col) {
        leaves.push({ f: sprite(64, leafDraw(shape, col.c, false)), b: sprite(64, leafDraw(shape, col.c, true)) });
      });
    });
    return { leaves: leaves };
  }

  /* ---------- Particles ---------- */
  function makeSnow(anywhere) {
    var z = Math.random();
    var p = { z: z, x: rand(-20, W + 20), y: anywhere ? rand(-20, H) : rand(-60, -20), ph: rand(0, 6.28), tw: rand(0.02, 0.05) };
    if (z < 0.55) {            /* far: tiny, slow, faint */
      p.kind = 'dot'; p.size = rand(3, 5); p.vy = rand(0.25, 0.45); p.a = rand(0.5, 0.75); p.sway = rand(0.15, 0.3);
    } else if (z < 0.86) {     /* mid: dots and small crystals */
      p.kind = Math.random() < 0.55 ? 'dot' : 'crystal'; p.size = p.kind === 'dot' ? rand(6, 9) : rand(13, 19);
      p.vy = rand(0.5, 0.85); p.a = rand(0.65, 0.9); p.sway = rand(0.25, 0.5);
    } else {                   /* near: large soft crystals, fast */
      p.kind = 'near'; p.size = rand(24, 34); p.vy = rand(1.0, 1.5); p.a = rand(0.45, 0.7); p.sway = rand(0.4, 0.7);
    }
    p.v = (Math.random() * 2) | 0;
    p.rot = rand(0, 6.28); p.spin = rand(-0.012, 0.012);
    return p;
  }
  function makeLeaf(anywhere) {
    var near = Math.random() < 0.35;
    var z = near ? rand(0.7, 1) : rand(0.15, 0.6);
    var ci = weightedColour();
    return {
      z: z,
      x0: rand(-40, W + 40),
      y: anywhere ? rand(-40, H) : rand(-120, -40),
      size: near ? rand(32, 46) : rand(18, 26),
      s: (((Math.random() * LEAVES.length) | 0) * LEAF_COLOURS.length) + ci,
      amp: rand(24, 64) * (0.6 + z * 0.6),
      w: rand(0.014, 0.026),
      p: rand(0, 6.28),
      vy: rand(0.55, 0.95) * (0.6 + z * 0.6),
      fp: rand(0, 6.28),
      fw: rand(0.018, 0.045),
      rot: rand(-0.6, 0.6),
      flutter: 0,
      a: near ? rand(0.85, 1) : rand(0.55, 0.8)
    };
  }

  function count() {
    var base = cfg.count || 24;
    var n = season === 'winter' ? base * 1.6 : base * 0.75;
    if (small) n *= 0.6;
    var area = Math.min(1.3, Math.max(0.6, Math.sqrt((W * H) / (1440 * 900))));
    return Math.max(6, Math.round(n * area));
  }
  function populate() {
    parts = [];
    var n = count();
    for (var i = 0; i < n; i++) parts.push(season === 'winter' ? makeSnow(true) : makeLeaf(true));
  }

  /* ---------- Loop ---------- */
  function draw(img, x, y, size, rot, sx, alpha) {
    var k = size / img.b;                  /* size maps to the glyph, padding scales with it */
    var c = Math.cos(rot), s = Math.sin(rot);
    ctx.globalAlpha = alpha;
    ctx.setTransform(dpr * c * sx * k, dpr * s * sx * k, -dpr * s * k, dpr * c * k, dpr * x, dpr * y);
    ctx.drawImage(img.c, -img.s / 2, -img.s / 2, img.s, img.s);
  }

  function frame(now) {
    var dt = last ? Math.min((now - last) / 16.67, 3) : 1;
    last = now;

    /* fade for play/pause */
    master += (masterTarget - master) * Math.min(1, 0.06 * dt);
    if (masterTarget === 0 && master < 0.01) { stopLoop(true); return; }

    /* wind gusts: ease toward a new target every 5 to 11 seconds */
    windTimer -= dt;
    if (windTimer <= 0) {
      windTarget = Math.random() < 0.18 ? rand(1.1, 1.8) * (Math.random() < 0.5 ? -1 : 1) : rand(-0.35, 0.6);
      windTimer = rand(300, 660);
    }
    wind += (windTarget - wind) * 0.006 * dt;

    var sd = scrollDelta; scrollDelta = 0;
    if (Math.abs(sd) > 400) sd = 0;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (var i = 0; i < parts.length; i++) {
      var p = parts[i], dx, dy, d;
      if (season === 'winter') {
        p.ph += 0.012 * dt;
        p.y += p.vy * dt - sd * (0.1 + p.z * 0.45);
        p.x += (Math.sin(p.ph) * p.sway + wind * (0.35 + p.z)) * dt;
        p.rot += p.spin * dt;
        if (finePointer && p.z > 0.4) {
          dx = p.x - px; dy = p.y - py; d = dx * dx + dy * dy;
          if (d < 8100) { d = Math.sqrt(d) || 1; p.x += dx / d * (1 - d / 90) * 2 * dt; p.y += dy / d * (1 - d / 90) * dt; }
        }
        if (p.y > H + 30) { parts[i] = makeSnow(false); continue; }
        if (p.y < -80) p.y = H + 20;
        if (p.x > W + 30) p.x = -30; else if (p.x < -30) p.x = W + 30;
        var tw = 0.85 + 0.15 * Math.sin(now * 0.001 * p.tw * 60 + p.ph);
        var img = p.kind === 'dot' ? (p.size > 6 ? sprites.dotSoft : sprites.dot)
          : p.kind === 'near' ? sprites.crystalsSoft[p.v] : sprites.crystals[p.v];
        draw(img, p.x, p.y, p.size, p.rot, 1, p.a * tw * master);
      } else {
        p.p += p.w * dt;
        var swing = Math.sin(p.p), swingV = Math.cos(p.p);
        /* falls fastest through the middle of the swing, floats at the ends */
        p.y += p.vy * (0.45 + 0.75 * Math.abs(swingV)) * dt - sd * (0.1 + p.z * 0.45);
        p.x0 += wind * (0.6 + p.z) * 1.2 * dt;
        p.fp += (p.fw + p.flutter * 0.25) * dt;
        p.flutter *= Math.pow(0.97, dt);
        if (Math.random() < 0.0015 * dt) p.flutter = 1;
        var x = p.x0 + swing * p.amp;
        if (finePointer) {
          dx = x - px; dy = p.y - py;
          if (dx * dx + dy * dy < 4900) { p.flutter = 1; p.x0 += (dx > 0 ? 1 : -1) * 1.2 * dt; }
        }
        if (p.y > H + 60) { parts[i] = makeLeaf(false); continue; }
        if (p.y < -160) p.y = H + 40;
        if (x > W + 70) p.x0 -= W + 140; else if (x < -70) p.x0 += W + 140;
        var flip = Math.cos(p.fp);
        var leaf = sprites.leaves[p.s];
        var rot = p.rot + swingV * 0.55 + p.flutter * Math.sin(p.fp * 3) * 0.6;
        draw(flip >= 0 ? leaf.f : leaf.b, x, p.y, p.size, rot, Math.max(0.12, Math.abs(flip)), p.a * master);
      }
    }
    ctx.globalAlpha = 1;
    raf = requestAnimationFrame(frame);
  }

  /* ---------- Setup, play, pause ---------- */
  function ensureCanvas() {
    if (canvas) return;
    canvas = document.createElement('canvas');
    canvas.className = 'winter-snow season-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    ctx = canvas.getContext('2d');
    sprites = buildSprites();
    resize();
    populate();
  }
  function resize() {
    var oW = W, oH = H;
    W = window.innerWidth; H = window.innerHeight;
    if (!canvas) return;
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
    if (oW && oH && parts.length) {
      parts.forEach(function (p) {
        if ('x0' in p) p.x0 *= W / oW; else p.x *= W / oW;
        p.y *= H / oH;
      });
    }
  }
  function startLoop() {
    if (running || document.hidden) return;
    running = true; last = 0;
    raf = requestAnimationFrame(frame);
  }
  function stopLoop(clear) {
    cancelAnimationFrame(raf); raf = 0; running = false;
    if (clear && ctx) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height); }
  }
  function play() { if (!ctx && canvas) return; ensureCanvas(); if (!ctx) return; masterTarget = 1; startLoop(); }
  function pause() { masterTarget = 0; if (!running) stopLoop(true); }

  function setState(on, save) {
    wantOn = on;
    if (save) writePref(on ? 'on' : 'off');
    if (on) play(); else pause();
    if (btn) {
      var noun = season === 'winter' ? 'snow' : 'leaves';
      var label = (on ? 'Pause ' : 'Play ') + noun;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', label);
      btn.title = label;
      var t = btn.querySelector('[data-fx-label]');
      if (t) t.textContent = label;
    }
  }

  var rt;
  window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(resize, 150); });
  window.addEventListener('scroll', function () {
    var y = window.scrollY; scrollDelta += y - lastScroll; lastScroll = y;
  }, { passive: true });
  if (finePointer) {
    window.addEventListener('pointermove', function (e) { px = e.clientX; py = e.clientY; }, { passive: true });
    document.addEventListener('pointerleave', function () { px = py = -9999; });
  }
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stopLoop(false);
    else if (wantOn) startLoop();
  });

  if (btn) {
    btn.hidden = false;
    btn.addEventListener('click', function () { setState(!wantOn, true); });
  }
  setState(wantOn, false);
})();
